import React, { useState } from "react";
import { Link } from "react-router-dom";
import SweetAlert from "react-bootstrap-sweetalert";
import PropTypes from "prop-types";
import {
  Tooltip,
  Typography,
  Divider,
  Box,
  IconButton,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Dialog,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Pagination from "components/Pagination/Pagination.js";
import SortIcon from "@material-ui/icons/Sort";
import SortByAlphaIcon from "@material-ui/icons/SortByAlpha";
import SearchBar from "material-ui-search-bar";

// styles
import sweetAlertStyle from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import TagDialog from "views/Components/TagDialog";
import AssetCard from "views/Components/AssetCard";
import InviteCollaboratorsModal from "views/Components/InviteCollaboratorsModal";
import { Add, BubbleChart, ExpandMore } from "@material-ui/icons";

// ---------- Styles (incl. hero + diagonal video pane) ----------

const useHeroStyles = makeStyles((theme) => ({
  heroWrap: {
    position: "relative",
    borderRadius: 24,
    overflow: "hidden",
    background:
      typeof quCreateColor === "string"
        ? `linear-gradient(135deg, rgba(32, 103, 123, 1) 0%, rgba(32, 103, 123, 0.85) 60%, rgba(32, 103, 123, 0.7) 100%)`
        : `linear-gradient(135deg, rgba(32, 103, 123, 1) 0%, rgba(32, 103, 123, 0.85) 60%, rgba(32, 103, 123, 0.7) 100%)`,
    color: "#fff",
    marginBottom: theme.spacing(1),
  },

  /** NEW: ensures both columns share height and the video can center vertically */
  heroGrid: {},

  heroInner: {
    position: "relative",
    zIndex: 2, // ensure above band overlay
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    [theme.breakpoints.up("md")]: { padding: theme.spacing(2) },
    display: "flex",
    flexDirection: "column",
    justifyContent: "center", // vertically center left content too
  },

  leftCol: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  title: { fontWeight: 700, letterSpacing: 0.2 },
  description: { opacity: 0.95, lineHeight: 1.6 },

  stepperRoot: {
    background: "transparent",
    "& .MuiStepLabel-label": { color: "#fff" },
    "& .MuiStepIcon-root": {
      color: "rgba(255,255,255,0.35)",
      "&.MuiStepIcon-active": { color: "#fff" },
      "&.MuiStepIcon-completed": { color: "rgba(255,255,255,0.85)" },
    },
  },

  /** Right video pane with diagonal left edge */
  videoPane: {
    position: "relative",
    zIndex: 2,
    padding: theme.spacing(1),
    paddingRight: theme.spacing(4),
    display: "flex",
    alignItems: "center", // <-- vertical centering
    justifyContent: "center", // <-- horizontal centering
    [theme.breakpoints.down("sm")]: {
      clipPath: "none",
    },
    zIndex: 1,
  },
  video: {
    width: "100%",
    border: "5px solid #fff",
    borderRadius: 24,
    maxHeight: "100%",
    objectFit: "cover", // keeps full frame visible
    filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.25))",
    zIndex: 1,
  },

  /** NEW: subtle diagonal bands between columns */
  bandOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    pointerEvents: "none",
    // layered diagonals; adjust stop positions to taste
    backgroundImage: [
      // main “divider” wedge
      `linear-gradient(135deg,
        transparent 0%,
        transparent 57%,
        rgba(32, 103, 123, 0.38) 57%,
        rgba(32, 103, 123, 0.38) 61%,
        transparent 31%
      )`,
      // thinner bands for depth
      `linear-gradient(135deg,
        transparent 0%,
        transparent 62%,
        rgba(32, 103, 123, 0.32) 62%,
        rgba(32, 103, 123, 0.32) 64%,
        transparent 34%
      )`,
      `linear-gradient(135deg,
        transparent 0%,
        transparent 66%,
        rgba(32, 103, 123, 0.28) 66%,
        rgba(32, 103, 123, 0.28) 68%,
        transparent 38%
      )`,
    ].join(","),

    media: {
      objectFit: "contain",
      borderTopLeftRadius: 18,
      borderTopRightRadius: 18,
    },
    mediaWrapper: {
      overflow: "hidden",
      borderTopLeftRadius: 18,
      borderTopRightRadius: 18,
    },
  },

  // examples (unchanged)
  examplesWrap: { border: "2px solid #20677b", borderRadius: 8 },
  exampleCard: {
    borderRadius: 18,
    height: "100%",
    background: "#fff",
  },
  exampleTitle: { fontWeight: 600 },
  exampleCta: { fontWeight: 600 },
}));

const usesweetAlertStyle = makeStyles(sweetAlertStyle);

// Simple example card component

const ExampleCard = ({
  mediaUrl,
  mediaAlt,
  title,
  description,
  cta = "Launch",
  openURL,
  link,
}) => {
  const classes = useHeroStyles();

  const isVideo = mediaUrl ? /\.(mp4|webm|ogg)$/i.test(mediaUrl) : false;

  const body = (
    <Card className={classes.exampleCard} elevation={3}>
      {/* Media on top (image or GIF) */}
      {mediaUrl && (
        <div className={classes.mediaWrapper}>
          {isVideo ? (
            <video
              className={classes.media}
              height="300px"
              style={{
                objectFit: "contain",
              }}
              src={mediaUrl}
              autoPlay
              muted
              loop
              playsInline
              controls={false}
            />
          ) : (
            <img
              className={classes.media}
              src={mediaUrl}
              height="250px"
              style={{
                objectFit: "contain",
                width: "100%",
              }}
              alt={mediaAlt || title}
            />
          )}
        </div>
      )}

      <CardContent>
        <Typography className={classes.exampleTitle} variant="subtitle1">
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" style={{ marginTop: 6, opacity: 0.8 }}>
            {description}
          </Typography>
        )}
      </CardContent>

      <CardActions
        style={{ paddingTop: 0, paddingLeft: 16, paddingBottom: 16 }}
      >
        <Button color="quCreateColor" onClick={openURL}>
          {cta}
        </Button>
      </CardActions>
    </Card>
  );

  return body;
};

const AssetDashboard = ({
  path,
  welcomeTitle, // still supported (fallback if workflowConfig.title not provided)
  welcomeSubtitle, // still supported (fallback)
  allAssets,
  setAllAssets,
  initialAssets,
  noAssetMessage,
  linkPath,
  state,
  getStatusIcon,
  CreateAssetCard,
  setCreateAssetModal,
  createAssetButtonText,
  handleSaveLabel,
  handleInviteSend,
  asset_identifier,
  handleDeleteAsset,
  handleCloneAsset,
  assetAdded,
  setAssetAdded,
  setInitialAssets,
  // NEW: landing/hero content
  workflowConfig, // { title, description, steps:[{label,body}], videoUrl, examples?:[] }
}) => {
  const hero = useHeroStyles();
  const sweetAlertClasses = usesweetAlertStyle();

  const [alert, setAlert] = React.useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const assetsPerPage = 6;

  const [ascendingOrder, setAscendingOrder] = useState(false);
  const [tagDialog, setTagDialog] = useState(false);
  const [tagSelectedAsset, setTagSelectedAsset] = useState(null);
  const [inviteCollarboratorsDialog, setInviteCollarboratorsDialog] = useState(
    false
  );
  const [
    inviteCollaboratorsSelectedAsset,
    setInviteCollaboratorsSelectedAsset,
  ] = useState(null);
  const [alphabeticallyInc, setAlphabeticallyInc] = useState(false);
  const [openVideoModal, setOpenVideoModal] = useState(false);

  const sortCards = () => {
    const sortedAssets = [...allAssets].sort((a, b) => {
      const dateA = new Date(parseInt(a._id.toString().slice(0, 8), 16) * 1000);
      const dateB = new Date(parseInt(b._id.toString().slice(0, 8), 16) * 1000);
      return ascendingOrder ? dateA - dateB : dateB - dateA;
    });
    setAllAssets(sortedAssets);
    setAscendingOrder(!ascendingOrder);
  };

  const sortCardsAlphabetically = () => {
    const sortedAssets = [...allAssets].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return alphabeticallyInc
        ? nameB.localeCompare(nameA)
        : nameA.localeCompare(nameB);
    });
    setAllAssets(sortedAssets);
    setAlphabeticallyInc(!alphabeticallyInc);
  };

  // Search
  const searchInputValue = (e) => {
    setSearchValue(e);
    if (!e?.length) {
      setAllAssets(initialAssets);
      return;
    }
    const q = e.toLowerCase();
    const newList = initialAssets.filter((asset) => {
      const nameMatch = asset.name && asset.name.toLowerCase().includes(q);
      const descriptionMatch =
        asset.description && asset.description.toLowerCase().includes(q);
      const tagMatch =
        asset.tags && asset.tags.some((tag) => tag.toLowerCase().includes(q));
      return nameMatch || descriptionMatch || tagMatch;
    });
    setAllAssets(newList);
    setCurrentPage(1);
  };

  // Pagination
  const indexOfLastAsset = currentPage * assetsPerPage;
  const indexOfFirstAsset = indexOfLastAsset - assetsPerPage;
  const currentAssets = allAssets.slice(indexOfFirstAsset, indexOfLastAsset);
  const totalPages = Math.ceil(allAssets.length / assetsPerPage);

  const pages = [];
  if (totalPages > 1) {
    pages.push({
      text: "PREV",
      disabled: currentPage === 1,
      onClick: () => setCurrentPage((prev) => Math.max(prev - 1, 1)),
    });
    for (let i = 1; i <= totalPages; i++) {
      pages.push({
        text: i,
        active: i === currentPage,
        onClick: () => setCurrentPage(i),
      });
    }
    pages.push({
      text: "NEXT",
      disabled: currentPage === totalPages,
      onClick: () => setCurrentPage((prev) => Math.min(prev + 1, totalPages)),
    });
  }

  if (assetAdded) {
    const totalPagesAfterAddition = Math.ceil(allAssets.length / assetsPerPage);
    if (currentPage < totalPagesAfterAddition) {
      setCurrentPage(Math.max(1, totalPagesAfterAddition));
    }
    setAssetAdded(false);
  }

  const handleTags = (asset) => {
    setTagSelectedAsset(asset);
    setTagDialog(true);
  };
  const handleCloseTagDialog = () => {
    setTagDialog(false);
    setTagSelectedAsset(null);
  };
  const handleInviteCollaborators = (asset) => {
    setInviteCollaboratorsSelectedAsset(asset);
    setInviteCollarboratorsDialog(true);
  };
  const handleCloseInviteCollaboratorsDialog = () => {
    setInviteCollarboratorsDialog(false);
    setInviteCollaboratorsSelectedAsset(null);
  };
  const handleSaveLabelHelper = async () => {
    setInitialAssets(
      allAssets.map((asset) =>
        asset._id === tagSelectedAsset._id ? tagSelectedAsset : asset
      )
    );
    await handleSaveLabel(tagSelectedAsset);
    handleCloseTagDialog();
  };
  const handleInviteSendHelper = async (data) => {
    try {
      await handleInviteSend(data);
      handleCloseInviteCollaboratorsDialog();
    } catch (error) {
      console.error("❌ Error sending invite:", error);
    }
  };

  const _handleDeleteAsset = async (asset) => {
    await handleDeleteAsset(asset);
    const totalPagesAfterDeletion = Math.ceil(
      (allAssets.length - 1) / assetsPerPage
    );
    if (currentPage > totalPagesAfterDeletion) {
      setCurrentPage(Math.max(1, totalPagesAfterDeletion));
    }
    hideAlert();
  };
  const _handleCloneAsset = async (asset) => {
    await handleCloneAsset(asset);
    hideAlert();
  };

  const deleteAlert = (asset) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => _handleDeleteAsset(asset)}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.success
        }
        cancelBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.danger
        }
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
      >
        This action will permanently delete the {asset_identifier} and will not
        be recoverable.
      </SweetAlert>
    );
  };
  const cloneAlert = (asset) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => _handleCloneAsset(asset)}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.success
        }
        cancelBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.danger
        }
        confirmBtnText="Yes, clone it!"
        cancelBtnText="Cancel"
        showCancel
      >
        This action will create a clone of the current {asset_identifier}.
      </SweetAlert>
    );
  };
  const hideAlert = () => setAlert(null);

  // Render
  const heroTitle = workflowConfig?.title || welcomeTitle;
  const heroDescription = workflowConfig?.description || welcomeSubtitle;
  const heroSteps = workflowConfig?.steps || [];
  const heroExamples = workflowConfig?.examples || [];
  const heroVideoUrl = workflowConfig?.videoUrl;

  return (
    <>
      {alert}

      {/* ---------- Landing / Hero Banner ---------- */}
      <Box className={hero.heroWrap}>
        {/* diagonal bands between left content and video */}
        <Box className={hero.bandOverlay} />

        <Grid container className={hero.heroGrid}>
          {/* Left column */}
          <Grid item xs={12} md={7}>
            <Box className={hero.heroInner + " " + hero.leftCol}>
              <Typography variant="h4" className={hero.title}>
                {heroTitle}
              </Typography>
              {heroDescription && (
                <Typography variant="body1" className={hero.description}>
                  {heroDescription}
                </Typography>
              )}

              {/* Stepper */}
              {heroSteps.length > 0 && (
                <Stepper
                  orientation="vertical"
                  nonLinear
                  className={hero.stepperRoot}
                  connector={null}
                >
                  {heroSteps.map((s, i) => (
                    <Step key={i} active>
                      <StepLabel>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 600, color: "#fff" }}
                        >
                          {s.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography
                          variant="body1"
                          style={{
                            color: "rgba(255,255,255,0.95)",
                            marginBottom: "16px",
                          }}
                        >
                          {s.body}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              )}
              <Button
                color="white"
                style={{
                  maxWidth: "50%",
                  fontWeight: "bold",
                  color: "#20677b",
                  bgcolor: "white",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1, // space between icon & text
                  lineHeight: 1,
                }}
                onClick={() => setCreateAssetModal(true)}
              >
                <Add />
                {createAssetButtonText}
              </Button>
            </Box>
          </Grid>

          {/* Right column (video) */}
          <Grid
            item
            xs={12}
            md={5}
            className={hero.videoPane}
            onClick={() => setOpenVideoModal(true)}
          >
            {heroVideoUrl ? (
              <video
                className={hero.video}
                src={heroVideoUrl}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
              />
            ) : (
              <Box p={3}>
                <Typography
                  variant="body2"
                  style={{ color: "rgba(255,255,255,0.9)" }}
                >
                  Add a <b>videoUrl</b> to your <i>workflowConfig</i> to show an
                  auto-playing demo here.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openVideoModal}
        onClose={() => setOpenVideoModal(false)}
        PaperProps={{
          style: {
            borderRadius: "24px",
            maxWidth: "80%",
            border: "10px solid #fff",
          },
        }}
      >
        <video src={heroVideoUrl} autoPlay muted loop playsInline />
      </Dialog>

      {/* Primed example cards (optional) */}
      {/* {heroExamples?.length > 0 && (
        <>
          <ExpansionPanel className={hero.examplesWrap}>
            <ExpansionPanelSummary expandIcon={<ExpandMore />}>
              <Typography
                variant="h6"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textTransform: "none",
                }}
              >
                <BubbleChart
                  style={{
                    color: "#20677b",
                    verticalAlign: "middle",
                    marginRight: 8,
                  }}
                />{" "}
                Highlighted Solutions
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Grid container spacing={2}>
                {heroExamples.slice(0, 3).map((ex, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <ExampleCard
                      {...ex}
                      openURL={() => window.open(ex.link, "_blank")}
                    />
                  </Grid>
                ))}
              </Grid>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        </>
      )} */}
      <Divider style={{ margin: "24px 0" }} />

      <Typography variant="h5" style={{ marginBottom: "16px" }}>
        {asset_identifier === "project" ? "Your Projects" : "Your Assets"}
      </Typography>

      {/* ---------- Search + Create ---------- */}
      <GridContainer>
        <GridItem xs={12}>
          <SearchBar
            value={searchValue}
            onChange={searchInputValue}
            onRequestSearch={searchInputValue}
            placeholder={`Search by name or tags`}
          />
        </GridItem>
      </GridContainer>

      {/* ---------- Sort Row ---------- */}
      <GridContainer>
        <GridItem
          item
          xs={12}
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            display: "flex",
            gap: "20px",
          }}
        >
          <Tooltip title="Sort by creation date" placement="top">
            <IconButton
              color="#20677b"
              style={{
                backgroundColor: ascendingOrder ? quCreateColor : "#f5f5f5",
              }}
            >
              <SortIcon onClick={sortCards} style={{ cursor: "pointer" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sort alphabetically" placement="top">
            <IconButton
              style={{
                backgroundColor: alphabeticallyInc ? quCreateColor : "#f5f5f5",
              }}
            >
              <SortByAlphaIcon
                onClick={sortCardsAlphabetically}
                style={{ cursor: "pointer" }}
              />
            </IconButton>
          </Tooltip>
        </GridItem>

        {/* ---------- Assets Grid ---------- */}
        {currentAssets.length === 0 && (
          <GridItem
            xs={12}
            sm={12}
            md={12}
            lg={12}
            style={{ marginTop: "20px" }}
          >
            <Box display="flex" justifyContent="center">
              <Typography variant="h5" textAlign="center">
                {noAssetMessage}
              </Typography>
            </Box>
          </GridItem>
        )}

        {currentAssets.map((asset, index) => (
          <GridItem key={index} xs={12} sm={6} md={6} lg={4}>
            <Link
              to={{
                pathname: path + linkPath + asset._id,
                state: state || undefined,
              }}
            >
              <AssetCard
                asset={asset}
                deleteAlert={deleteAlert}
                cloneAlert={cloneAlert}
                handleTags={handleTags}
                handleInviteCollaborators={handleInviteCollaborators}
                getStatusIcon={getStatusIcon}
              />
            </Link>
          </GridItem>
        ))}

        {currentPage === totalPages && currentAssets.length < assetsPerPage && (
          <GridItem xs={12} sm={6} md={6} lg={4}>
            <CreateAssetCard onClick={() => setCreateAssetModal(true)} />
          </GridItem>
        )}
      </GridContainer>

      {/* ---------- Pagination ---------- */}
      {totalPages > 1 && (
        <GridContainer
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "20px",
          }}
        >
          <Pagination pages={pages} color="quCreateColor" />
        </GridContainer>
      )}

      {/* ---------- Modals ---------- */}
      <TagDialog
        tagDialog={tagDialog}
        handleCloseTagDialog={handleCloseTagDialog}
        tagSelectedAsset={tagSelectedAsset}
        setTagSelectedAsset={setTagSelectedAsset}
        noAssetMessage={
          "No tags assigned. Add new tags to organize your assets."
        }
        handleSaveLabel={handleSaveLabelHelper}
      />
      <InviteCollaboratorsModal
        open={inviteCollarboratorsDialog}
        handleClose={handleCloseInviteCollaboratorsDialog}
        inviteCollaboratorsSelectedAsset={inviteCollaboratorsSelectedAsset}
        setInviteCollaboratorsSelectedAsset={
          setInviteCollaboratorsSelectedAsset
        }
        noAssetMessage={
          "No collaborators assigned. Invite new collaborators to your asset."
        }
        handleInviteSend={handleInviteSendHelper}
      />
    </>
  );
};

// ---------- PropTypes ----------
AssetDashboard.propTypes = {
  path: PropTypes.string.isRequired,
  welcomeTitle: PropTypes.string.isRequired,
  welcomeSubtitle: PropTypes.string.isRequired,
  allAssets: PropTypes.array.isRequired,
  setAllAssets: PropTypes.func.isRequired,
  initialAssets: PropTypes.array.isRequired,
  noAssetMessage: PropTypes.string.isRequired,
  linkPath: PropTypes.string.isRequired,
  state: PropTypes.object,
  getStatusIcon: PropTypes.func.isRequired,
  CreateAssetCard: PropTypes.func.isRequired,
  setCreateAssetModal: PropTypes.func.isRequired,
  createAssetButtonText: PropTypes.string.isRequired,
  handleSaveLabel: PropTypes.func.isRequired,
  handleInviteSend: PropTypes.func.isRequired,
  asset_identifier: PropTypes.string.isRequired,
  handleDeleteAsset: PropTypes.func.isRequired,
  handleCloneAsset: PropTypes.func.isRequired,
  assetAdded: PropTypes.bool.isRequired,
  setAssetAdded: PropTypes.func.isRequired,
  setInitialAssets: PropTypes.func.isRequired,
  // NEW:
  workflowConfig: PropTypes.shape({
    type: PropTypes.oneOf([
      "lab",
      "course",
      "research",
      "podcast",
      "model_dev",
      "model_val",
      "writing",
      "template_form",
    ]),
    title: PropTypes.string,
    description: PropTypes.string,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        body: PropTypes.string.isRequired,
      })
    ),
    videoUrl: PropTypes.string,
    examples: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        cta: PropTypes.string,
        onClick: PropTypes.func,
        link: PropTypes.string,
      })
    ),
  }),
};

export default AssetDashboard;
